/**
 * useSubscriptionSync — reconcile the local subscription store with the server.
 *
 * Without this, the ONLY writer of client premium state is the optimistic
 * `purchase:complete` event dispatched from the CdvPurchase `approved` handler,
 * persisted to localStorage and never re-checked. That splits the client from
 * the server in both directions:
 *
 *   - a lapsed / refunded / revoked subscriber keeps client-gated Premium
 *     forever while every server-gated call (friend location) 403s;
 *   - a paying subscriber who reinstalls or signs in on a second device shows
 *     Free until they manually tap Restore, even though the SUBSCRIPTION row
 *     entitles them;
 *   - a Restore of an expired subscription flips the UI to Premium permanently
 *     even though apple-verify refused it with 200 {success:false}.
 *
 * Canon (shared-docs/in-app-purchases.md): "Clients must treat 200
 * {success:false} as NOT linked" and "Refetch AFTER the backend write
 * resolves." GET /subscription/status is the single source of truth; the
 * `subscription:refresh` event is dispatched by services/payments.js only once
 * Promise.allSettled of the apple-verify writes has settled.
 *
 * Mounted ONCE, in App.jsx — useSubscription() has ~8 consumers and must not
 * each run their own reconcile.
 */

import { useEffect, useCallback } from 'react';
import { getSubscriptionStatus } from '../services/api';
import useSubscriptionStore from '../stores/useSubscriptionStore';

export default function useSubscriptionSync({ userId, isAuthed }) {
  const reconcile = useCallback(async () => {
    if (!isAuthed || !userId) return;
    let status;
    try {
      status = await getSubscriptionStatus();
    } catch {
      // Offline, 401 mid-refresh, 5xx — keep whatever is persisted rather than
      // downgrading a paying user on a chairlift with no signal.
      return;
    }
    // null = no session resolved; "unknown", not "not subscribed".
    if (!status) return;

    const entitled = Boolean(status.active) && status.plan && status.plan !== 'free';
    const store = useSubscriptionStore.getState();
    if (entitled) {
      store.setSubscription({
        tier: 'premium',
        source: status.source === 'apple' ? 'iap_apple' : status.source || 'none',
        status: 'active',
        expiresAt: status.expiresAt || null,
      });
    } else {
      // Server says not linked — revoke the optimistic grant.
      store.reset();
    }
  }, [userId, isAuthed]);

  // App start / sign-in / sign-out.
  useEffect(() => {
    reconcile();
  }, [reconcile]);

  // After the apple-verify writes settle (purchase or restore).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('subscription:refresh', reconcile);
    return () => window.removeEventListener('subscription:refresh', reconcile);
  }, [reconcile]);

  // Foreground — picks up an expiry/refund the S2S webhook applied while the
  // app was backgrounded. Same pattern as services/sync/engine.js.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => {
      if (document.visibilityState === 'visible') reconcile();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [reconcile]);
}
